import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';

export const useSmartCardElementsRenderState = ({
	isOwnerInfoShown,
	isModifiedOnShown,
	isSocialStatsShown,
}: {
	isModifiedOnShown?: boolean;
	isOwnerInfoShown?: boolean;
	isSocialStatsShown?: boolean;
}): {
	setIsAvatarAvailable: Dispatch<SetStateAction<boolean | undefined>>;
	setIsCommentCountAvailable: Dispatch<SetStateAction<boolean | undefined>>;
	setIsModifiedOnAvailable: Dispatch<SetStateAction<boolean | undefined>>;
	setIsOwnerNameAvailable: Dispatch<SetStateAction<boolean | undefined>>;
	setIsReactCountAvailable: Dispatch<SetStateAction<boolean | undefined>>;
	shouldRenderMetadata: boolean | undefined;
	shouldRenderOwnerInfo: boolean | undefined;
	shouldRenderSocials: boolean | undefined;
} => {
	// smart card elements render states
	const [isAvatarAvailable, setIsAvatarAvailable] = useState<boolean>();
	const [isOwnerNameAvailable, setIsOwnerNameAvailable] = useState<boolean>();
	const [isModifiedOnAvailable, setIsModifiedOnAvailable] = useState<boolean>();
	const [isCommentCountAvailable, setIsCommentCountAvailable] = useState<boolean>();
	const [isReactCountAvailable, setIsReactCountAvailable] = useState<boolean>();

	useEffect(() => {
		// attempt to render the elements when they are supposed to be shown to get data availability
		setIsAvatarAvailable(!!isOwnerInfoShown);
		setIsOwnerNameAvailable(!!isOwnerInfoShown);
		setIsModifiedOnAvailable(!!isModifiedOnShown);
		setIsCommentCountAvailable(!!isSocialStatsShown);
		setIsReactCountAvailable(!!isSocialStatsShown);
	}, [isOwnerInfoShown, isModifiedOnShown, isSocialStatsShown]);

	const shouldRenderOwnerInfo = useMemo(
		() => isAvatarAvailable || isOwnerNameAvailable,
		[isAvatarAvailable, isOwnerNameAvailable],
	);
	const shouldRenderMetadata = useMemo(
		() => shouldRenderOwnerInfo || isModifiedOnAvailable,
		[shouldRenderOwnerInfo, isModifiedOnAvailable],
	);
	const shouldRenderSocials = useMemo(
		() => isReactCountAvailable || isCommentCountAvailable,
		[isReactCountAvailable, isCommentCountAvailable],
	);

	return {
		setIsAvatarAvailable,
		setIsOwnerNameAvailable,
		setIsModifiedOnAvailable,
		setIsReactCountAvailable,
		setIsCommentCountAvailable,
		shouldRenderOwnerInfo,
		shouldRenderMetadata,
		shouldRenderSocials,
	};
};
